import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { ChevronLeft, ChevronRight, ShoppingCart, Sparkles } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import api from '../services/api';

const RecommendedProducts = ({ currentProductId, categoryId }) => {
  const { currentLanguage, t: tg } = useLanguage();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const translations = {
    en: { title: 'You May Also Like', subtitle: 'Similar products from the same category', addToCart: 'Add to Cart', taxIncluded: 'Tax Included' },
    es: { title: 'También Te Puede Gustar', subtitle: 'Productos similares de la misma categoría', addToCart: 'Añadir al Carrito', taxIncluded: 'Impuestos Incluidos' },
    ru: { title: 'Вам Также Может Понравиться', subtitle: 'Похожие продукты из той же категории', addToCart: 'Добавить в Корзину', taxIncluded: 'Налоги Включены' },
    de: { title: 'Das könnte Sie auch interessieren', subtitle: 'Ähnliche Produkte aus derselben Kategorie', addToCart: 'In den Warenkorb', taxIncluded: 'inkl. MwSt.' },
    fr: { title: 'Vous pourriez aussi aimer', subtitle: 'Produits similaires de la même catégorie', addToCart: 'Ajouter au panier', taxIncluded: 'Taxes incluses' },
    it: { title: 'Potrebbe interessarti anche', subtitle: 'Prodotti simili della stessa categoria', addToCart: 'Aggiungi al carrello', taxIncluded: 'IVA inclusa' },
    pl: { title: 'Może Ci się również spodobać', subtitle: 'Podobne produkty z tej samej kategorii', addToCart: 'Dodaj do koszyka', taxIncluded: 'Podatek VAT wliczony' },
    nl: { title: 'Mogelijk vindt u dit ook leuk', subtitle: 'Vergelijkbare producten uit dezelfde categorie', addToCart: 'In winkelwagen', taxIncluded: 'Incl. btw' },
    pt: { title: 'Você também pode gostar', subtitle: 'Produtos semelhantes da mesma categoria', addToCart: 'Adicionar ao carrinho', taxIncluded: 'Impostos incluídos' },
  };

  const t = translations[currentLanguage] || translations.en;

  useEffect(() => {
    const fetchRecommendedProducts = async () => {
      if (!categoryId) {
        console.log('RecommendedProducts: No categoryId provided');
        setLoading(false);
        return;
      }

      try {
        console.log('RecommendedProducts: Fetching products for category:', categoryId);
        const response = await api.get('/products', {
          params: {
            category_id: categoryId,
            limit: 20
          }
        });

        console.log('RecommendedProducts: API response:', response.data);
        
        let categoryProducts = Array.isArray(response.data) 
          ? response.data 
          : response.data?.products || [];
        
        console.log('RecommendedProducts: Found products:', categoryProducts.length);
        
        categoryProducts = categoryProducts.filter(p => p.id !== currentProductId);
        console.log('RecommendedProducts: After filtering current product:', categoryProducts.length);

        if (categoryProducts.length > 0) {
          const sorted = [...categoryProducts].sort((a, b) => a.price - b.price);
          const mixed = [];
          let left = 0;
          let right = sorted.length - 1;
          let fromLeft = true;

          while (left <= right) {
            if (fromLeft) {
              mixed.push(sorted[left]);
              left++;
            } else {
              mixed.push(sorted[right]);
              right--;
            }
            fromLeft = !fromLeft;
          }

          setProducts(mixed);
          console.log('RecommendedProducts: Final mixed products:', mixed.length);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching recommended products:', error);
        setLoading(false);
      }
    };

    fetchRecommendedProducts();
  }, [categoryId, currentProductId]);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, [products]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getProductName = (product) => {
    if (!product.name) return 'Product';
    return product.name[currentLanguage] || product.name.en || product.name.es || 'Product';
  };

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    const variant = product.variants?.[0];
    await addToCart(product.id, 1, variant?.name || null);
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-12 bg-capsule-bg">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-capsule-teal"></div>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className="py-10 sm:py-16 bg-gradient-to-b from-capsule-bg to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-capsule-teal/20 shadow-sm">
            <Sparkles className="w-4 h-4 text-capsule-teal" />
            <span className="text-sm font-medium text-capsule-teal">{tg('rec_section_title')}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t.title}</h2>
          <p className="text-gray-500 text-sm sm:text-base">{t.subtitle}</p>
        </div>

        {/* Scrollable Container */}
        <div className="relative group">
          {/* Left Scroll Button */}
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-capsule-teal hover:text-white border border-gray-100 ${
              canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label={tg('rec_scroll_left')}
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Products Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-1"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="flex-shrink-0 w-[200px] sm:w-[240px] lg:w-[280px] group/card"
                data-testid={`recommended-product-${product.id}`}
              >
                <div className="glass-card rounded-3xl shadow-glass hover:shadow-glass-hover transition-all duration-300 overflow-hidden h-full flex flex-col">
                  {/* Product Image */}
                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                    <img
                      src={product.image_url}
                      alt={getProductName(product)}
                      className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {/* Quick Add Button */}
                    <button
                      onClick={(e) => handleAddToCart(e, product)}
                      className="absolute bottom-3 right-3 w-10 h-10 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal rounded-xl flex items-center justify-center text-white shadow-teal-glow opacity-0 group-hover/card:opacity-100 transition-all duration-300 transform translate-y-2 group-hover/card:translate-y-0"
                      aria-label={t.addToCart}
                    >
                      <ShoppingCart className="w-5 h-5" />
                    </button>
                    
                    {/* Category Badge */}
                    {product.product_type && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-white/90 backdrop-blur-sm text-capsule-teal text-xs font-medium px-3 py-1 rounded-full border border-capsule-teal/20">
                          {product.product_type[currentLanguage] || product.product_type.en || ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4 sm:p-5 flex flex-col flex-grow bg-white/50">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 mb-2 group-hover/card:text-capsule-teal transition-colors">
                      {getProductName(product)}
                    </h3>
                    
                    <div className="mt-auto">
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-capsule-teal to-capsule-aqua bg-clip-text text-transparent">
                          {formatPrice(product.price)}
                        </span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-xs sm:text-sm text-gray-400 line-through">
                            {formatPrice(product.original_price)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{t.taxIncluded}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Right Scroll Button */}
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-capsule-teal hover:text-white border border-gray-100 ${
              canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-label={tg('rec_scroll_right')}
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Scroll Indicator Dots (Mobile) */}
        <div className="flex justify-center gap-2 mt-6 sm:hidden">
          {products.slice(0, Math.min(5, Math.ceil(products.length / 2))).map((_, index) => (
            <div
              key={index}
              className="w-2 h-2 rounded-full bg-capsule-teal/30"
            />
          ))}
        </div>
      </div>

      {/* Hide scrollbar CSS */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default RecommendedProducts;
